import {
  SupplierProductGetResponseType,
  ProductTemplateType,
} from "@sellar-io/api-v2/types";
import { useRef, useState } from "react";
import { Dialog } from "../../../../components/modal/Dialog";
import ItemDetailsDialogContent, {
  ProductCreationItemDetailsType,
} from "./components/ItemDetailsDialogContent";
import StockManagementDialogContent from "./components/StockManagementDialogContent";
import ItemReleaseDialogContent, {
  ItemReleaseType,
} from "./components/ItemReleaseDialogContent";
import ItemPricingDialogContent from "./components/ItemPricingDialogContent";
import { useProductCreate } from "../../../../hooks/api/v2/product/useProductCreate";
import { useProductPriceListBatchCreate } from "../../../../hooks/api/v2/productPriceList/useProductPriceListBatchCreate";
import { ProductTemplateDialogContent } from "./components/ProductTemplateDialogContent";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";
import ItemVisibilityDialogContent from "./components/ItemVisibilityDialogContent";
import { ProductPriceListBatchCreateRequestType } from "@sellar-io/api-v2/types";
import { useProductAvailabilityTierBatchCreate } from "../../../../hooks/api/v2/productAvailabilityTier/useProductAvailabilityTierBatchCreate";
import ItemPricingUnbundledDialogContent from "./components/ItemPricingUnbundledDialogContent";
import NoBasePriceListContent from "./components/NoBasePriceListContent";
import { usePriceListBaseList } from "../../../../hooks/api/v2/priceList/usePriceListBaseList";
import ItemDeliveryMethodRulesDialogContent from "./components/ItemDeliveryMethodRulesDialogContent";
import { useDeliveryMethodContainerRulesContainerTypes } from "../../../../hooks/api/v2/deliveryMethodContainerRules/useDeliveryMethodContainerRulesContainerTypes";
import { ProductItemDialogPages } from "./CreateProductItemDialog.shared";
import { useProductCatalogueMode } from "../../../../hooks/useProductCatalogueMode";

export type ProductItemDialogPagesType = {
  PRODUCT_TEMPLATE: number;
  ITEM_DETAILS: number;
  ITEM_RELEASE: number;
  STOCK_MANAGEMENT: number;
  ITEM_PRICING: number;
  ITEM_PRICING_UNBUNDLED: number;
  ITEM_VISIBILITY: number;
  ITEM_DELIVERY_METHOD_RULES: number;
  NO_BASE_LIST: number;
};

export default function CreateProductItemDialog({
  active,
  onDismiss,
  parentProduct,
  defaultTemplate,
  skipTemplateSelection = false,
}: {
  active: boolean;
  parentProduct: SupplierProductGetResponseType;
  onDismiss: (productId?: number) => void;
  defaultTemplate?: ProductTemplateType;
  skipTemplateSelection?: boolean;
}) {
  const [itemRelease, setItemRelease] = useState<ItemReleaseType>({
    releaseType: "generalRelease",
    availableFrom: null,
  });
  const [step, setStep] = useState<number>(
    defaultTemplate || skipTemplateSelection
      ? ProductItemDialogPages.ITEM_DETAILS
      : ProductItemDialogPages.PRODUCT_TEMPLATE,
  );
  const [productCreationItemDetails, setProductCreationItemDetails] =
    useState<ProductCreationItemDetailsType>();
  const [template, setTemplate] = useState<ProductTemplateType | undefined>(
    defaultTemplate,
  );

  const [createdProductId, setCreatedProductId] = useState<number | undefined>(
    undefined,
  );

  const [updatedPriceListItems, setUpdatedPriceListItems] = useState<
    ProductPriceListBatchCreateRequestType | undefined
  >(undefined);
  const updatedPriceListItemsRef = useRef<
    ProductPriceListBatchCreateRequestType | undefined
  >(undefined);

  const updatePriceListItems = (
    items: ProductPriceListBatchCreateRequestType | undefined,
  ) => {
    updatedPriceListItemsRef.current = items;
    setUpdatedPriceListItems(items);
  };

  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const productPriceListBatchCreateMutation = useProductPriceListBatchCreate();
  const createProductMutation = useProductCreate();
  const productAvailabilityTierBatchCreateMutation =
    useProductAvailabilityTierBatchCreate();
  const productCatalogueMode = useProductCatalogueMode();

  const unbundlingRelease2BEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2B",
  );

  const unbundlingEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );

  const { data: basePriceListsData, isFetched: basePriceListsFetched } =
    usePriceListBaseList(
      {
        limit: 50,
      },
      { enabled: unbundlingRelease2BEnabled && active },
    );

  const hasBasePriceLists =
    (basePriceListsData?.pages.flatMap((page) => page) ?? []).length > 0;

  const { data: existingContainerTypes } =
    useDeliveryMethodContainerRulesContainerTypes({
      enabled: unbundlingEnabled,
    });

  const shouldShowNoBasePriceList =
    unbundlingRelease2BEnabled && basePriceListsFetched && !hasBasePriceLists;

  const handleDismiss = () => {
    const createdId = createdProductId;
    setCreatedProductId(undefined);
    onDismiss(createdId);
  };

  const isNewContainerType = () => {
    if (!productCreationItemDetails || !existingContainerTypes) return false;

    const key = `${productCreationItemDetails.containerType}-${productCreationItemDetails.volume}-${productCreationItemDetails.unit}`;

    return !existingContainerTypes.some(
      (ct) => `${ct.containerType}-${ct.volume}-${ct.displayUnit}` === key,
    );
  };

  const advanceAfterVisibility = () => {
    if (unbundlingEnabled && isNewContainerType()) {
      setStep(ProductItemDialogPages.ITEM_DELIVERY_METHOD_RULES);
    } else {
      handleDismiss();
    }
  };

  const completeItemSetup = async (availabilityTierIds: number[]) => {
    if (!createdProductId) return;

    const mutations: Array<Promise<unknown>> = [];

    if (availabilityTierIds.length > 0) {
      mutations.push(
        productAvailabilityTierBatchCreateMutation.mutateAsync(
          availabilityTierIds.map((AvailabilityTierId) => ({
            AvailabilityTierId,
            ProductId: createdProductId,
            active: true,
          })),
        ),
      );
    }

    const priceListItems = updatedPriceListItemsRef.current;
    if (priceListItems && priceListItems.length > 0) {
      mutations.push(
        productPriceListBatchCreateMutation.mutateAsync(priceListItems),
      );
    }

    try {
      await Promise.all(mutations);
    } catch (error) {
      console.error("One or more mutations failed:", error);
      return;
    }

    setSelectedIds([]);
    updatePriceListItems(undefined);
    advanceAfterVisibility();
  };

  const continueFromPricing = () => {
    if (productCatalogueMode.isBasicMode) {
      // The API attaches active items to the default catalogue in basic mode.
      // Creating that assignment again causes a duplicate-record failure.
      void completeItemSetup([]);
      return;
    }

    setStep(ProductItemDialogPages.ITEM_VISIBILITY);
  };

  return (
    <Dialog active={active} onDismiss={handleDismiss}>
      {shouldShowNoBasePriceList ? (
        <NoBasePriceListContent onDismiss={handleDismiss} />
      ) : (
        <>
          {step === ProductItemDialogPages.PRODUCT_TEMPLATE && (
            <ProductTemplateDialogContent
              onDismiss={handleDismiss}
              setTemplate={setTemplate}
              next={() => setStep(ProductItemDialogPages.ITEM_DETAILS)}
            />
          )}

          {step === ProductItemDialogPages.ITEM_DETAILS && (
            <ItemDetailsDialogContent
              productCreationItemDetails={{
                vegan: parentProduct.vegan,
                lactoseFree: parentProduct.lactoseFree,
                glutenFree: parentProduct.glutenFree,
                ...productCreationItemDetails,
              }}
              onBack={() => setStep(ProductItemDialogPages.PRODUCT_TEMPLATE)}
              onDismiss={handleDismiss}
              onSubmitProductItemDetails={(productDetails) => {
                setProductCreationItemDetails(productDetails);
                setStep(ProductItemDialogPages.ITEM_RELEASE);
              }}
              template={template}
            />
          )}

          {step === ProductItemDialogPages.ITEM_RELEASE && (
            <ItemReleaseDialogContent
              itemRelease={itemRelease}
              onSubmitItemReleaseType={(itemRelease) => {
                setItemRelease(itemRelease);
                setStep(ProductItemDialogPages.STOCK_MANAGEMENT);
              }}
              onDismiss={handleDismiss}
              onBack={() => setStep(ProductItemDialogPages.ITEM_DETAILS)}
            />
          )}

          {step === ProductItemDialogPages.STOCK_MANAGEMENT &&
            productCreationItemDetails && (
              <StockManagementDialogContent
                itemRelease={itemRelease}
                loading={createProductMutation.isPending}
                onDismiss={handleDismiss}
                onBack={() => {
                  setStep(ProductItemDialogPages.ITEM_RELEASE);
                }}
                onSubmitStockManagementDetails={(stockManagementDetails) => {
                  if (createdProductId) {
                    if (unbundlingRelease2BEnabled) {
                      setStep(ProductItemDialogPages.ITEM_PRICING_UNBUNDLED);
                    } else {
                      setStep(ProductItemDialogPages.ITEM_PRICING);
                    }
                  } else {
                    createProductMutation.mutate(
                      {
                        ...productCreationItemDetails,
                        ...stockManagementDetails,
                        availableFrom: itemRelease.availableFrom,
                        type: "productItem",
                        ParentId: parentProduct.id,
                      },
                      {
                        onSuccess: (createdProduct) => {
                          setCreatedProductId(createdProduct.id);
                          if (unbundlingRelease2BEnabled) {
                            setStep(
                              ProductItemDialogPages.ITEM_PRICING_UNBUNDLED,
                            );
                          } else {
                            setStep(ProductItemDialogPages.ITEM_PRICING);
                          }
                        },
                      },
                    );
                  }
                }}
              />
            )}

          {step === ProductItemDialogPages.ITEM_PRICING && createdProductId && (
            <ItemPricingDialogContent
              productId={createdProductId}
              loading={
                productPriceListBatchCreateMutation.isPending ||
                productAvailabilityTierBatchCreateMutation.isPending
              }
              onDismiss={(nextStep?: boolean) => {
                if (nextStep) {
                  continueFromPricing();
                } else {
                  updatePriceListItems(undefined);
                  handleDismiss();
                }
              }}
              updatedPriceListItems={updatedPriceListItems}
              setUpdatedPriceListItems={updatePriceListItems}
              onSubmit={() => {
                continueFromPricing();
              }}
            />
          )}

          {unbundlingRelease2BEnabled &&
            step === ProductItemDialogPages.ITEM_PRICING_UNBUNDLED &&
            createdProductId && (
              <ItemPricingUnbundledDialogContent
                productId={createdProductId}
                containerType={productCreationItemDetails?.containerType}
                loading={
                  productPriceListBatchCreateMutation.isPending ||
                  productAvailabilityTierBatchCreateMutation.isPending
                }
                setUpdatedPriceListItems={updatePriceListItems}
                onSubmit={() => {
                  continueFromPricing();
                }}
              />
            )}

          {step === ProductItemDialogPages.ITEM_VISIBILITY &&
            createdProductId && (
              <ItemVisibilityDialogContent
                productId={createdProductId}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                onDismiss={handleDismiss}
                onBack={() => {
                  if (unbundlingRelease2BEnabled) {
                    setStep(ProductItemDialogPages.ITEM_PRICING_UNBUNDLED);
                  } else {
                    setStep(ProductItemDialogPages.ITEM_PRICING);
                  }
                }}
                onSubmit={() => void completeItemSetup(selectedIds)}
              />
            )}
          {unbundlingEnabled &&
            step === ProductItemDialogPages.ITEM_DELIVERY_METHOD_RULES &&
            createdProductId &&
            productCreationItemDetails && (
              <ItemDeliveryMethodRulesDialogContent
                onDismiss={handleDismiss}
                onBack={() => setStep(ProductItemDialogPages.ITEM_VISIBILITY)}
                onSubmit={() => {
                  handleDismiss();
                }}
                containerType={productCreationItemDetails.containerType}
                volume={productCreationItemDetails.volume}
                displayUnit={productCreationItemDetails.unit}
              />
            )}
        </>
      )}
    </Dialog>
  );
}
